import { useMemo, useState } from 'react';
import { format, addMonths } from 'date-fns';
import {
  useTheme,
  useMediaQuery,
  Box,
  Typography,
  Chip,
  Checkbox,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
} from '@mui/material';
import { AddCircleOutline } from '@mui/icons-material';


const EmployeeNeedTraining = ({
  matrixData,
  subjectId,
  subjectName,
  rankFilter,
  lookAheadMonths,
  onAddToBuilder,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selected, setSelected] = useState(new Set());

  const combined = useMemo(() => {
    if (!matrixData || !subjectId) return [];
    const { employees } = matrixData;
    const now = new Date();
    const horizon = addMonths(now, lookAheadMonths || 3);
    const sid = subjectId.toString();

    const rows = [];
    for (const emp of employees) {
      if (rankFilter && emp.rank !== rankFilter) continue;

      const qual = emp.qualifications?.[sid];

      let urgencyLabel, urgencyColor, urgencyOrder, expiryDate = null;

      if (!qual || qual.status === 'missing') {
        urgencyLabel = 'Never trained';
        urgencyColor = 'error';
        urgencyOrder = 0;
      } else if (qual.status === 'expired') {
        expiryDate = qual.expiryDate;
        urgencyLabel = 'Overdue';
        urgencyColor = 'error';
        urgencyOrder = 1;
      } else if (qual.status === 'expiring-soon') {
        expiryDate = qual.expiryDate;
        const days = expiryDate
          ? Math.ceil((new Date(expiryDate) - now) / (1000 * 60 * 60 * 24))
          : null;
        urgencyLabel = days != null ? `Expires in ${days}d` : 'Expiring';
        urgencyColor = 'warning';
        urgencyOrder = 2;
      } else if (qual.expiryDate && new Date(qual.expiryDate) <= horizon) {
        // valid but within planning horizon
        expiryDate = qual.expiryDate;
        const days = Math.ceil((new Date(expiryDate) - now) / (1000 * 60 * 60 * 24));
        urgencyLabel = `Expires in ${days}d`;
        urgencyColor = 'warning';
        urgencyOrder = 3;
      } else {
        continue; // up to date beyond horizon
      }

      rows.push({
        employeeCode: emp.employeeCode,
        employeeName: emp.employeeName || '',
        rank: emp.rank || '',
        base: emp.base || '',
        expiryDate,
        urgencyLabel,
        urgencyColor,
        urgencyOrder,
      });
    }

    return rows.sort((a, b) =>
      a.urgencyOrder - b.urgencyOrder || Number(a.employeeCode) - Number(b.employeeCode)
    );
  }, [matrixData, subjectId, rankFilter, lookAheadMonths]);

  // Reset selection when subject changes
  useMemo(() => { setSelected(new Set()); }, []);

  const toggleSelect = (employeeCode) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(employeeCode)) next.delete(employeeCode);
      else next.add(employeeCode);
      return next;
    });

  const selectAll = () => {
    if (selected.size === combined.length) setSelected(new Set());
    else setSelected(new Set(combined.map((r) => r.employeeCode)));
  };

  const handleAddToBuilder = () => {
    const selectedCrew = combined.filter((r) => selected.has(r.employeeCode));
    onAddToBuilder?.(selectedCrew);
  };

  if (!subjectId) {
    return (
      <Typography color="text.secondary" p={2}>
        Select a subject from the heatmap or the filter above to see crew needing training.
      </Typography>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={1}
      >
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Typography variant="subtitle2" color="textSecondary">
            {subjectName || 'Subject'} - {combined.length} employee{combined.length !== 1 ? 's' : ''} need training
          </Typography>
          {rankFilter && (
            <Chip label={`Rank: ${rankFilter}`} size="small" variant="outlined" color="primary" />
          )}
        </Box>
        {combined.length > 0 && (
          <Button
            size="small" variant="outlined" startIcon={<AddCircleOutline />}
            onClick={handleAddToBuilder}
            disabled={selected.size === 0}
            fullWidth={isMobile}
          >
            Add {selected.size} to Class Builder
          </Button>
        )}
      </Box>

      {combined.length === 0 ? (
        <Typography color="text.secondary" variant="body2">
          All employees are up to date for this subject in the next {lookAheadMonths} months.
        </Typography>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 400, overflowY: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={selected.size === combined.length && combined.length > 0}
                    indeterminate={selected.size > 0 && selected.size < combined.length}
                    onChange={selectAll}
                  />
                </TableCell>
                <TableCell>Employee</TableCell>
                <TableCell>Rank</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Base</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Last Expiry</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {combined.map((row) => (
                <TableRow key={row.employeeCode} selected={selected.has(row.employeeCode)}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selected.has(row.employeeCode)}
                      onChange={() => toggleSelect(row.employeeCode)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">{row.employeeCode}</Typography>
                    <Typography variant="caption" color="text.secondary">{row.employeeName}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={row.rank || '-'} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <Typography variant="body2">{row.base || '-'}</Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <Typography variant="body2">
                      {row.expiryDate ? format(new Date(row.expiryDate), 'dd/MM/yyyy') : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={row.urgencyLabel} size="small" color={row.urgencyColor} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default EmployeeNeedTraining;
